import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { readShigoAppConfig } from '../../../lib/config';
import { useShigoSession } from '../../../lib/useShigoSession';
import { createNetworkTemplateClient } from './api';
import type {
  A2vConfiguration,
  A2vLivePreviewRequest,
  NetworkTemplateConfiguration,
} from './model';

export const networkTemplateQueryKey = ['a2v', 'network-template'] as const;
export const publisherTemplateQueryKey = ['a2v', 'publisher-template'] as const;

function useNetworkTemplateClient() {
  const { session } = useShigoSession();
  const config = readShigoAppConfig();
  if (!session) throw new Error('Shigo session is required.');
  return {
    client: createNetworkTemplateClient({
      config: {
        apiBaseUrl: config.apiBaseUrl,
        networkId: config.networkId,
        a2vUrl: config.a2vUrl,
      },
      accessToken: session.accessToken,
    }),
    cdnUrl: config.cdnUrl,
  };
}

export function useNetworkTemplateQuery() {
  const { client } = useNetworkTemplateClient();
  return useQuery({
    queryKey: networkTemplateQueryKey,
    queryFn: client.getConfiguration,
  });
}

export function useA2vTemplateQuery(publisherId?: string) {
  const { client } = useNetworkTemplateClient();
  return useQuery({
    queryKey: publisherId ? [...publisherTemplateQueryKey, publisherId] : networkTemplateQueryKey,
    queryFn: () =>
      publisherId ? client.getPublisherConfiguration(publisherId) : client.getConfiguration(),
  });
}

export function useNetworkTemplateMutations() {
  const { client, cdnUrl } = useNetworkTemplateClient();
  const queryClient = useQueryClient();
  return {
    cdnUrl,
    update: useMutation({
      mutationFn: (configuration: A2vConfiguration) => client.updateConfiguration(configuration),
      onSuccess: (saved) => {
        queryClient.setQueryData<NetworkTemplateConfiguration>(
          networkTemplateQueryKey,
          (current) => ({
            ...current,
            a2vConfiguration: saved,
          }),
        );
      },
    }),
    // react-doctor-disable-next-line react-doctor/query-mutation-missing-invalidation -- returns a generated QR code asset URL; no cached query shows it
    generateQrCode: useMutation({
      mutationFn: (text: string) => client.generateQrCode(text),
    }),
    createLivePreview: useMutation({
      mutationFn: (request: A2vLivePreviewRequest) => client.createLivePreview(request),
    }),
  };
}

export function useA2vTemplateMutations(publisherId?: string) {
  const { client, cdnUrl } = useNetworkTemplateClient();
  const queryClient = useQueryClient();
  const queryKey = publisherId
    ? [...publisherTemplateQueryKey, publisherId]
    : networkTemplateQueryKey;
  const update = useMutation({
    mutationFn: ({
      configuration,
      useCustomTemplate,
      publisher,
    }: {
      configuration: A2vConfiguration;
      useCustomTemplate: boolean;
      publisher?: unknown;
    }) =>
      publisherId
        ? client.updatePublisherConfiguration(publisherId, {
            a2vConfiguration: configuration,
            useCustomTemplate,
            publisher,
          })
        : client.updateConfiguration(configuration),
    onSuccess: (saved, variables) => {
      queryClient.setQueryData<NetworkTemplateConfiguration>(queryKey, (current) => ({
        ...current,
        a2vConfiguration: saved,
        useCustomTemplate: variables.useCustomTemplate,
        publisher: variables.publisher ?? current?.publisher,
      }));
    },
  });
  return {
    cdnUrl,
    update,
    // react-doctor-disable-next-line react-doctor/query-mutation-missing-invalidation -- returns a generated QR code asset URL; no cached query shows it
    generateQrCode: useMutation({
      mutationFn: (text: string) => client.generateQrCode(text),
    }),
    createLivePreview: useMutation({
      mutationFn: (request: A2vLivePreviewRequest) => client.createLivePreview(request),
    }),
  };
}
